// Google Ads REAL Service - używa prawdziwego API
import {
  AdCampaign,
  AdGroup,
  Ad,
  Keyword,
  CampaignStats,
  KeywordResearch,
  BudgetRecommendation,
  CampaignOptimization
} from '../types/googleAds.types';

const API_URL = 'https://api.plexisystem.pl';

export class GoogleAdsRealService {
  private async fetchAPI(endpoint: string, options?: RequestInit) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }

    return response.json();
  }

  // Pobierz PRAWDZIWE kampanie z Google Ads API
  async getCampaigns(filters?: {
    status?: string;
    type?: string;
  }): Promise<AdCampaign[]> {
    try {
      // Używamy endpointu który nie wymaga autoryzacji
      const data = await this.fetchAPI('/api/google-ads/campaigns-simple');
      
      if (!data || !data.campaigns) {
        console.warn('No campaigns data from API, returning empty array');
        return [];
      }

      // Mapuj dane z Google Ads API na nasz format
      const campaigns: AdCampaign[] = data.campaigns.map((campaign: any) => ({
        id: campaign.id || campaign.resourceName,
        name: campaign.name || 'Bez nazwy',
        type: campaign.type || 'SEARCH',
        status: this.mapStatus(campaign.status),
        budget_daily: campaign.budget || campaign.budgetDaily || 0,
        budget_total: campaign.budgetTotal || null,
        currency: campaign.currency || 'PLN',
        start_date: campaign.startDate || new Date().toISOString(),
        end_date: campaign.endDate || null,
        bidding_strategy: campaign.biddingStrategy || 'MANUAL_CPC',
        targeting: {
          locations: campaign.targetLocations || [],
          languages: campaign.targetLanguages || ['pl'],
          keywords: campaign.keywords || [],
          demographics: campaign.demographics || {},
          devices: campaign.devices || ['DESKTOP', 'MOBILE', 'TABLET']
        },
        metrics: {
          impressions: campaign.impressions || 0,
          clicks: campaign.clicks || 0,
          cost: campaign.cost || 0,
          conversions: campaign.conversions || 0,
          ctr: campaign.ctr || 0,
          cpc: campaign.avgCpc || campaign.cpc || 0,
          conversion_rate: campaign.conversionRate || 0
        },
        created_at: campaign.createdAt || new Date().toISOString(),
        updated_at: campaign.updatedAt || new Date().toISOString()
      }));

      // Filtruj jeśli potrzeba
      let result = campaigns;
      if (filters?.status) {
        result = result.filter(c => c.status === filters.status);
      }
      if (filters?.type) {
        result = result.filter(c => c.type === filters.type);
      }

      return result;
    } catch (error) {
      console.error('Error fetching real campaigns:', error);
      
      // Zwróć dane z localStorage jako fallback
      const cached = localStorage.getItem('googleAdsCampaigns');
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      
      return [];
    }
  }

  private mapStatus(googleStatus: string): 'active' | 'paused' | 'removed' | 'draft' {
    const statusMap: Record<string, 'active' | 'paused' | 'removed' | 'draft'> = {
      'ENABLED': 'active',
      'PAUSED': 'paused',
      'REMOVED': 'removed',
      'UNKNOWN': 'draft',
      'UNSPECIFIED': 'draft'
    };
    return statusMap[googleStatus] || 'draft';
  }

  async getCampaign(id: string): Promise<AdCampaign> {
    const campaigns = await this.getCampaigns();
    const campaign = campaigns.find(c => c.id === id);
    if (!campaign) {
      throw new Error(`Campaign ${id} not found`);
    }
    return campaign;
  }

  // Grupy reklam
  async getAdGroups(campaignId: string): Promise<AdGroup[]> {
    try {
      const data = await this.fetchAPI(`/api/google-ads/campaigns/${campaignId}/ad-groups`);
      return data.adGroups || [];
    } catch (error) {
      console.error('Error fetching ad groups:', error);
      return [];
    }
  }

  // Słowa kluczowe
  async getKeywords(adGroupId: string): Promise<Keyword[]> {
    try {
      const data = await this.fetchAPI(`/api/google-ads/ad-groups/${adGroupId}/keywords`);
      return data.keywords || [];
    } catch (error) {
      console.error('Error fetching keywords:', error);
      return [];
    }
  }

  // Reklamy
  async getAds(adGroupId: string): Promise<Ad[]> {
    try {
      const data = await this.fetchAPI(`/api/google-ads/ad-groups/${adGroupId}/ads`);
      return data.ads || [];
    } catch (error) {
      console.error('Error fetching ads:', error);
      return [];
    }
  }

  // Statystyki
  async getCampaignStats(campaignId: string, dateFrom: Date, dateTo: Date): Promise<CampaignStats[]> {
    try {
      const params = new URLSearchParams({
        from: dateFrom.toISOString().split('T')[0],
        to: dateTo.toISOString().split('T')[0]
      });
      const data = await this.fetchAPI(`/api/google-ads/campaigns/${campaignId}/stats?${params}`);
      return data.stats || [];
    } catch (error) {
      console.error('Error fetching campaign stats:', error);
      return [];
    }
  }

  async getAggregatedStats(campaignIds: string[], dateFrom: Date, dateTo: Date): Promise<{
    totalImpressions: number;
    totalClicks: number;
    totalCost: number;
    totalConversions: number;
    avgCtr: number;
    avgCpc: number;
    roas: number;
  }> {
    // Pobierz kampanie żeby mieć aktualne statystyki
    const campaigns = await this.getCampaigns();
    
    const relevantCampaigns = campaigns.filter(c => campaignIds.includes(c.id));
    
    const totalImpressions = relevantCampaigns.reduce((sum, c) => sum + (c.metrics?.impressions || 0), 0);
    const totalClicks = relevantCampaigns.reduce((sum, c) => sum + (c.metrics?.clicks || 0), 0);
    const totalCost = relevantCampaigns.reduce((sum, c) => sum + (c.metrics?.cost || 0), 0);
    const totalConversions = relevantCampaigns.reduce((sum, c) => sum + (c.metrics?.conversions || 0), 0);

    return {
      totalImpressions,
      totalClicks,
      totalCost,
      totalConversions,
      avgCtr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
      avgCpc: totalClicks > 0 ? totalCost / totalClicks : 0,
      roas: totalCost > 0 ? (totalConversions * 100) / totalCost : 0 // Zakładamy 100 PLN per konwersja
    };
  }

  // Metody CREATE/UPDATE/DELETE - na razie stub, docelowo przez API
  async createCampaign(campaign: Omit<AdCampaign, 'id' | 'created_at' | 'updated_at'>): Promise<AdCampaign> {
    const data = await this.fetchAPI('/api/google-ads/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaign)
    });
    return data;
  }

  async updateCampaign(id: string, updates: Partial<AdCampaign>): Promise<AdCampaign> {
    const data = await this.fetchAPI(`/api/google-ads/campaigns/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
    return data;
  }

  async deleteCampaign(id: string): Promise<void> {
    await this.fetchAPI(`/api/google-ads/campaigns/${id}`, {
      method: 'DELETE'
    });
  }

  async createAdGroup(adGroup: Omit<AdGroup, 'id' | 'created_at'>): Promise<AdGroup> {
    const data = await this.fetchAPI('/api/google-ads/ad-groups', {
      method: 'POST',
      body: JSON.stringify(adGroup)
    });
    return data;
  }

  async updateAdGroup(id: string, updates: Partial<AdGroup>): Promise<AdGroup> {
    const data = await this.fetchAPI(`/api/google-ads/ad-groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
    return data;
  }

  async createAd(ad: Omit<Ad, 'id' | 'created_at'>): Promise<Ad> {
    const data = await this.fetchAPI('/api/google-ads/ads', {
      method: 'POST',
      body: JSON.stringify(ad)
    });
    return data;
  }

  async updateAd(id: string, updates: Partial<Ad>): Promise<Ad> {
    const data = await this.fetchAPI(`/api/google-ads/ads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
    return data;
  }

  async deleteAd(id: string): Promise<void> {
    await this.fetchAPI(`/api/google-ads/ads/${id}`, {
      method: 'DELETE'
    });
  }

  async addKeywords(keywords: Omit<Keyword, 'id'>[]): Promise<Keyword[]> {
    const data = await this.fetchAPI('/api/google-ads/keywords', {
      method: 'POST',
      body: JSON.stringify({ keywords })
    });
    return data.keywords;
  }

  async updateKeyword(id: string, updates: Partial<Keyword>): Promise<Keyword> {
    const data = await this.fetchAPI(`/api/google-ads/keywords/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
    return data;
  }

  // Badanie słów kluczowych
  async researchKeywords(seedKeywords: string[]): Promise<KeywordResearch[]> {
    try {
      const data = await this.fetchAPI('/api/google-ads/keyword-research', {
        method: 'POST',
        body: JSON.stringify({ keywords: seedKeywords })
      });
      return data.research || [];
    } catch (error) {
      // Fallback na symulację
      return seedKeywords.map(keyword => ({
        keyword,
        search_volume: Math.floor(Math.random() * 10000) + 100,
        competition: 'medium' as const,
        suggested_bid: 2.5,
        related_keywords: [`${keyword} gdańsk`, `${keyword} cena`]
      }));
    }
  }

  // Rekomendacje budżetu
  async getBudgetRecommendation(
    keywords: string[],
    targetLocation: string,
    monthlyBudget: number
  ): Promise<BudgetRecommendation> {
    try {
      const data = await this.fetchAPI('/api/google-ads/budget-recommendation', {
        method: 'POST',
        body: JSON.stringify({ keywords, targetLocation, monthlyBudget })
      });
      return data;
    } catch (error) {
      // Fallback
      const dailyBudget = monthlyBudget / 30;
      return {
        daily_budget: dailyBudget,
        monthly_budget: monthlyBudget,
        estimated_clicks: Math.floor(dailyBudget * 10),
        estimated_impressions: Math.floor(dailyBudget * 200),
        estimated_conversions: Math.floor(dailyBudget * 0.5),
        recommended_bid_strategy: 'Manual CPC'
      };
    }
  }

  // Optymalizacje
  async getCampaignOptimizations(campaignId: string): Promise<CampaignOptimization> {
    try {
      const data = await this.fetchAPI(`/api/google-ads/campaigns/${campaignId}/optimizations`);
      return data;
    } catch (error) {
      // Fallback
      return {
        campaign_id: campaignId,
        recommendations: [
          {
            type: 'budget',
            priority: 'medium',
            description: 'Rozważ zwiększenie budżetu',
            estimated_impact: '+20% kliknięć',
            action_items: ['Zwiększ budżet o 20%', 'Monitoruj ROI']
          }
        ]
      };
    }
  }

  // Eksport
  async exportCampaigns(campaignIds: string[], format: 'csv' | 'xlsx'): Promise<Blob> {
    const campaigns = await Promise.all(
      campaignIds.map(id => this.getCampaign(id))
    );

    if (format === 'csv') {
      const headers = ['Nazwa', 'Typ', 'Status', 'Budżet dzienny', 'Wyświetlenia', 'Kliknięcia', 'Koszt', 'CTR', 'CPC'];
      const rows = campaigns.map(c => [
        c.name,
        c.type,
        c.status,
        c.budget_daily.toString(),
        (c.metrics?.impressions || 0).toString(),
        (c.metrics?.clicks || 0).toString(),
        (c.metrics?.cost || 0).toFixed(2),
        (c.metrics?.ctr || 0).toFixed(2) + '%',
        (c.metrics?.cpc || 0).toFixed(2)
      ]);

      const csv = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      return new Blob([csv], { type: 'text/csv;charset=utf-8' });
    }

    throw new Error('XLSX export not implemented yet');
  }
}

// Eksportuj singleton
export const googleAdsRealService = new GoogleAdsRealService();
